import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert } from "antd";
import AppShell from "../../../components/layout/AppShell";
import StatCard from "../../../components/ui/StatCard";
import InvoiceList from "./InvoiceList";
import { getInvoices, getBanks, listSenders } from "../../../services/api";
import { formatAmount, getMaxDecimalsForCurrency, getDateFilterRange } from "../../../utils";
import { DATE_FILTER_OPTIONS } from "../../../utils/constants";
import AnalyticsSummaryCards from "../../../components/ui/AnalyticsSummaryCards";
import { getPaymentRequestAnalytics } from "../../../services/analytics.api";
import useAnalytics from "../../../hooks/useAnalytics";
import { INVOICE_ANALYTICS_CONFIG } from "../../../config/transactionAnalytics.config";
const ANALYTICS_ENABLED = true;

// Kill-switch for this page's invoice-list <DateFilter />: hides the UI and
// stops sending from/to params to the API.
// TODO: remove once the backend supports date-range filtering on receiver invoices.
const DISABLE_DATE_FILTER = false;

// Kill-switch for this page's analytics <DateFilter />.
const DISABLE_DATE_FILTER_FOR_ANALYTICS = false;

const STATUS_MAP = {
  "Paid":        "success",
  "In Progress": "processing",
  "Pending":     "created",
};

const fmtCcy = (amount, currency = "USD") => {
  const n = Number(amount);
  if (isNaN(n)) return "—";
  const ccy = (currency || "USD").toUpperCase();
  return `${ccy} ${formatAmount(amount, getMaxDecimalsForCurrency(ccy))}`;
};
const fmt = (n) => fmtCcy(n, "USD");

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const Overview = () => {
  const navigate = useNavigate();
  const userId = useSelector((s) => s.auth.userId);

  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounce(search, 400);
  const [dateRange, setDateRange] = useState(() => getDateFilterRange(DATE_FILTER_OPTIONS.MONTH_TILL_DATE));

  // null = still checking, true = linked, false = none linked
  const [hasBankAccount, setHasBankAccount] = useState(null);
  const [hasSenders, setHasSenders] = useState(null);


  // Analytics summary (feature-flagged)
  const [analyticsDateRange, setAnalyticsDateRange] = useState(() =>
    getDateFilterRange(DATE_FILTER_OPTIONS.MONTH_TILL_DATE)
  );
  const {
    value: invoiceAnalyticsItems,
    loading: invoiceAnalyticsLoading,
  } = useAnalytics(
    getPaymentRequestAnalytics,
    DISABLE_DATE_FILTER_FOR_ANALYTICS
      ? {}
      : { from: analyticsDateRange.startDate, to: analyticsDateRange.endDate },
    { enabled: ANALYTICS_ENABLED }
  );

  // ── Load bank + sender status (once) ─────────────────────────────────────
  useEffect(() => {
    if (!userId) return;

    getBanks(userId)
      .then((res) => {
        const banks = res?.data?.banks || [];
        setHasBankAccount(banks.length > 0);
      })
      .catch(() => setHasBankAccount(false));

    listSenders()
      .then((res) => {
        const senders = res?.data || [];
        setHasSenders(senders.length > 0);
      })
      .catch(() => setHasSenders(false));
  }, [userId]);

  // Reset to page 1 whenever the search query settles
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // ── Load invoices (server-paginated) ─────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    setError("");
    const params = { page, limit: pageSize };
    if (filter !== "All") params.status = STATUS_MAP[filter];
    if (debouncedSearch) params.searchQuery = debouncedSearch;
    if (!DISABLE_DATE_FILTER) {
      if (dateRange.startDate) params.from = dateRange.startDate;
      if (dateRange.endDate) params.to = dateRange.endDate;
    }
    getInvoices(userId, params)
      .then((res) => {
        const data = res?.data || [];
        setInvoices(data);
        setTotal(res?.meta?.total || data.length);
      })
      .catch(() => {
        setInvoices([]);
        setError("Failed to load invoices. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [userId, page, pageSize, filter, debouncedSearch, dateRange]);

  const changeFilter = (t) => {
    setFilter(t);
    setPage(1);
  };

  const changePageSize = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const changeDateFilter = (_nextFilter, range) => {
    setDateRange((prev) =>
      prev.startDate === range.startDate && prev.endDate === range.endDate ? prev : range
    );
    setPage(1);
  };

  // Analytics-sourced totals — pagination-independent, unlike summing the fetched page
  const billedTotal = ["open", "paid", "pending", "failed", "rejected"]
    .reduce((s, k) => s + Number(invoiceAnalyticsItems?.[k]?.amount || 0), 0);

  const analyticsItems = (invoiceAnalyticsItems != null) ? INVOICE_ANALYTICS_CONFIG.map(({ key, title, valueClassName }) => {
    if (key === "overDueCount") {
      return {
        title,
        value: invoiceAnalyticsItems?.[key] || 0,
        valueClassName,
      }
    }

    const data = invoiceAnalyticsItems?.[key] || [];
    const count = invoiceAnalyticsItems?.[key]?.count;
    if (!count) return null;
    return {
      title,
      prefix: data?.currency?.toUpperCase(),
      prefixClassName: valueClassName,
      value: formatAmount(data?.amount, getMaxDecimalsForCurrency(data?.currency)),
      subLabel: `${count ?? 0} Transactions`,
      valueClassName,
    };
  }).filter(Boolean) : [];

  return (
    <AppShell>
      {/* Step 1: Add-bank banner — highest priority, shown when no bank account */}
      {hasBankAccount === false && (
        <div className="flex items-center justify-between px-4 py-3 bg-green-50 border border-green-200 rounded-xl mb-3">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-green-700">
              Your account is verified. Please add your bank account to proceed.
            </span>
          </div>
          <button
            onClick={() => navigate("/receiver/overview/add-bank")}
            className="ml-6 flex-shrink-0 bg-brand text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-brandHover transition-colors"
          >
            Add Bank Account
          </button>
        </div>
      )}

      {/* Step 2: No-senders banner — only shown after bank is set up */}
      {hasBankAccount !== false && hasSenders === false && (
        <div className="flex items-center justify-between px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl mb-5">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-sm text-orange-700">
              No senders added yet. Please add a sender to start creating invoices.
            </span>
          </div>
          <button
            onClick={() => navigate("/receiver/senders/create")}
            className="ml-6 flex-shrink-0 bg-orange-500 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Add Sender
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            View and manage all invoices
          </p>
        </div>
        <button
          onClick={() => navigate("/receiver/invoices/create")}
          disabled={hasBankAccount === false || hasSenders === false}
          title={
            hasBankAccount === false
              ? "Add a bank account first"
              : hasSenders === false
              ? "Add a sender first"
              : undefined
          }
          className="bg-brand text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-brandHover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          + Create Invoice
        </button>
      </div>

      {/* Analytics summary (feature-flagged) */}
      {analyticsItems.length > 0 && (
        <AnalyticsSummaryCards
          items={analyticsItems}
          loading={invoiceAnalyticsLoading}
          showDateFilter={!DISABLE_DATE_FILTER_FOR_ANALYTICS}
          onDateFilterChange={(_filter, range) =>
            setAnalyticsDateRange((prev) =>
              prev.startDate === range.startDate && prev.endDate === range.endDate ? prev : range
            )
          }
        />
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Invoices" value={loading ? "—" : total.toString()} />
        <StatCard label="Billed" value={invoiceAnalyticsLoading ? "—" : fmt(billedTotal)} />
        <StatCard
          label="Paid"
          value={
            invoiceAnalyticsLoading
              ? "—"
              : fmtCcy(invoiceAnalyticsItems?.paid?.amount, invoiceAnalyticsItems?.paid?.currency)
          }
        />
        <StatCard
          label="Awaiting"
          value={invoiceAnalyticsLoading ? "—" : `${invoiceAnalyticsItems?.open?.count ?? 0} invoices`}
          valueColor="text-orange-500"
        />
      </div>

      {/* Invoice table card */}
      {error && <Alert type="error" message={error} showIcon className="mb-4" />}
      <InvoiceList
        data={invoices}
        loading={loading}
        total={total}
        page={page}
        pageSize={pageSize}
        filter={filter}
        onFilterChange={changeFilter}
        search={search}
        onSearchChange={setSearch}
        onPageChange={setPage}
        onPageSizeChange={changePageSize}
        disableDateFilter={DISABLE_DATE_FILTER}
        onDateFilterChange={changeDateFilter}
      />
    </AppShell>
  );
};

export default Overview;
